import {useEagGridStore} from '../store/useEagGridStore';
import type {ActionCallback} from '../types/ActionCallback';
import type {RowData} from '../types/RowData';
import {executeBulkAction} from '../utils/executeBulkAction';

interface UseBulkActionExecutionOptions {
  actionCallback: ActionCallback<readonly string[]> | undefined;
  rows: RowData[];
  selectedRowIds: Set<string>;
  gridCode: string;
  /** Stable refresh callback — reads from refs at call time, does not change identity. */
  refreshData: () => void;
}

/**
 * Returns a callback that executes a bulk action by calling `actionCallback`
 * with an array of selected row IDs and their data.
 *
 * If the callback returns a Promise, the hook sets `isBulkActionLoading` in the
 * store to `true` until the Promise settles. While loading, subsequent calls are
 * silently ignored (double-click protection).
 *
 * No manual useCallback — React Compiler memoizes this hook's return value automatically.
 *
 * Delegates to `executeBulkAction` — the shared execution core also used by the
 * external invoke channel, so both paths run bulk actions identically.
 */
export function useBulkActionExecution(options: UseBulkActionExecutionOptions) {
  const store = useEagGridStore();

  return (actionKey: string) =>
    executeBulkAction({
      store,
      actionCallback: options.actionCallback,
      rows: options.rows,
      rowIds: options.rows.filter((row) => options.selectedRowIds.has(row.id)).map((row) => row.id),
      refreshData: options.refreshData,
      actionKey,
    });
}
